import React from 'react';
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';

/**
 * Form Select Field Component
 *
 * Reusable select input field with label, optional icon, and error message.
 * Designed to be used with React Hook Form Controller.
 *
 * @param {FormSelectFieldProps} props - Component properties
 * @returns {React.ReactElement} Form select field component
 *
 * @example
 * <Controller
 *   control={methods.control}
 *   name="roleUuid"
 *   render={({ field, fieldState }) => (
 *     <FormSelectField
 *       field={field}
 *       error={fieldState.error?.message}
 *       label="Rol"
 *       placeholder="Selecciona un rol"
 *       options={roles}
 *       getOptionValue={(role) => role.uuid}
 *       getOptionLabel={(role) => role.name}
 *       required
 *     />
 *   )}
 * />
 */

interface FormSelectFieldProps<T = any> {
  field: any;
  label: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  id?: string;
  disabled?: boolean;
  options: T[];
  getOptionValue: (option: T) => string;
  getOptionLabel: (option: T) => string;
  getOptionKey?: (option: T) => string;
  labelIcon?: React.ReactNode;
  labelClassName?: string;
  onValueChange?: (value: string) => void;
  triggerClassName?: string;
}

const styles = {
  container: 'flex flex-col gap-1',
  required: 'text-destructive',
  errorMessage: 'text-xs text-destructive',
  error: 'border-destructive',
};

export const FormSelectField = <T,>({
  field,
  label,
  placeholder,
  required = false,
  error,
  id,
  disabled = false,
  options,
  getOptionValue,
  getOptionLabel,
  getOptionKey,
  labelIcon,
  labelClassName,
  onValueChange,
  triggerClassName,
}: FormSelectFieldProps<T>): React.ReactElement => {
  const handleValueChange = (value: string) => {
    if (onValueChange) {
      onValueChange(value);
    }
    field.onChange(value);
  };

  return (
    <div className={styles.container}>
      <Label className={cn(labelClassName)} htmlFor={id}>
        {label} {required && <span className={styles.required}>*</span>}
        {labelIcon}
      </Label>
      <Select disabled={disabled} onValueChange={handleValueChange} value={field.value ?? ''}>
        <SelectTrigger className={cn(triggerClassName, !disabled && !!error && styles.error)} id={id}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => {
            const value = getOptionValue(option);
            const label = getOptionLabel(option);
            const key = getOptionKey ? getOptionKey(option) : value;

            return (
              <SelectItem key={key} value={value}>
                {label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {error && <p className={styles.errorMessage}>{error}</p>}
    </div>
  );
};
